import { useState, useCallback, useMemo, useRef } from 'react';
import { Link, router } from '@inertiajs/react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, format } from 'date-fns';
import { useHotkeys } from 'react-hotkeys-hook';
import { Toaster, toast } from 'sonner';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import Avatar from '@/Components/Avatar';
import EmptyState from '@/Components/EmptyState';
import Pagination from '@/Components/Pagination';
import SearchInput from '@/Components/SearchInput';
import ConfirmModal from '@/Components/ConfirmModal';

// Fixed-position row actions dropdown — escapes all overflow containers
function RowActions({ actions }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);

    const handleOpen = () => {
        if (open) { setOpen(false); return; }
        const rect = triggerRef.current.getBoundingClientRect();
        const menuWidth = 190;
        const menuHeight = actions.length * 38 + 8;
        // Position below trigger, aligned to right edge
        let top = rect.bottom + 4;
        let left = rect.right - menuWidth;
        // If menu would go off left edge, align to left of trigger
        if (left < 8) left = rect.left;
        // If menu would go below viewport, open above
        if (top + menuHeight > window.innerHeight - 8) {
            top = rect.top - menuHeight - 4;
        }
        setPos({ top, left });
        setOpen(true);
    };

    return (
        <div className="qi-row-actions">
            <button ref={triggerRef} className="qi-row-actions-trigger" onClick={handleOpen}>
                <i className="bi bi-three-dots-vertical"></i>
            </button>
            {open && (
                <>
                    <div className="qi-row-actions-backdrop" onClick={() => setOpen(false)} />
                    <div className="qi-row-actions-menu" style={{ top: pos.top, left: pos.left }}>
                        {actions.map((action, i) => {
                            if (action.divider) return <div key={i} className="qi-action-divider" />;
                            const cls = `qi-action-item${action.whatsapp ? ' action-whatsapp' : ''}${action.danger ? ' action-danger' : ''}`;
                            if (action.href) {
                                return (
                                    <a key={i} href={action.href} target={action.target} rel={action.rel} className={cls} onClick={() => setOpen(false)}>
                                        {action.icon && <i className={`bi ${action.icon}`}></i>}
                                        {action.label}
                                    </a>
                                );
                            }
                            return (
                                <button key={i} className={cls} onClick={() => { action.onClick?.(); setOpen(false); }}>
                                    {action.icon && <i className={`bi ${action.icon}`}></i>}
                                    {action.label}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

const STATUS_HEX = {
    all: '#051a52',
    pending: '#f69f00',
    proposal_sent: '#c20e97',
    followup: '#0d84f0',
    confirmed: '#1cb94c',
    completed: '#10b981',
    lost: '#8f8b8b',
    cancelled: '#cf1010',
    reverted: '#654c92',
};

const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatDate = (date) => {
    if (!date) return '-';
    try {
        return format(new Date(date), 'dd MMM yy');
    } catch {
        return '-';
    }
};

const cleanPhone = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/[\s\-()]/g, '');
    if (cleaned.startsWith('0')) cleaned = '91' + cleaned.slice(1);
    if (!cleaned.startsWith('+') && !cleaned.startsWith('91') && cleaned.length === 10) {
        cleaned = '91' + cleaned;
    }
    return cleaned.replace(/^\+/, '');
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '34px',
        fontSize: '0.8125rem',
        borderColor: state.isFocused ? '#2563eb' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(37,99,235,0.15)' : 'none',
        '&:hover': { borderColor: '#94a3b8' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '0.8125rem' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    option: (base, state) => ({
        ...base,
        fontSize: '0.8125rem',
        padding: '6px 10px',
        backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
        color: state.isSelected ? 'white' : '#334155',
    }),
    menu: (base) => ({ ...base, zIndex: 20 }),
    placeholder: (base) => ({ ...base, fontSize: '0.8125rem', color: '#94a3b8' }),
    singleValue: (base) => ({ ...base, fontSize: '0.8125rem' }),
};

export default function QueriesIndex({
    queries,
    users,
    statuses,
    statusColors,
    types,
    priorities,
    priorityColors,
    sources,
    travelTypes,
    statusCounts,
    quickFilterCounts,
    destinationNames,
    filters,
}) {
    const [selected, setSelected] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [queryToDelete, setQueryToDelete] = useState(null);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
    const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

    // Keyboard shortcuts
    useHotkeys('n', (e) => { e.preventDefault(); router.visit('/queries/create'); }, { enableOnFormTags: false });
    useHotkeys('/', (e) => { e.preventDefault(); document.querySelector('.search-input input')?.focus(); }, { enableOnFormTags: false });
    useHotkeys('escape', () => { if (hasActiveFilters) router.get('/queries'); }, { enableOnFormTags: true });

    const totalCount = useMemo(() =>
        Object.values(statusCounts).reduce((a, b) => a + b, 0),
    [statusCounts]);

    const hasActiveFilters = useMemo(() =>
        Object.entries(filters).some(([k, v]) => v && k !== 'per_page' && k !== 'sort' && k !== 'direction'),
    [filters]);

    const getAssignedTo = useCallback((query) => {
        if (typeof query.assigned_to === 'object' && query.assigned_to !== null) {
            return query.assigned_to;
        }
        return null;
    }, []);

    const getCreatedBy = useCallback((query) => {
        return query.created_by && typeof query.created_by === 'object' ? query.created_by : null;
    }, []);

    // Filter handlers
    const applyFilter = useCallback((key, value) => {
        const newFilters = { ...filters, [key]: value || undefined };
        // If applying a quick filter, clear status and vice versa
        if (key === 'quick') { delete newFilters.status; }
        if (key === 'status') { delete newFilters.quick; }
        Object.keys(newFilters).forEach(k => { if (!newFilters[k]) delete newFilters[k]; });
        router.get('/queries', newFilters, { preserveState: true, replace: true });
    }, [filters]);

    const clearFilters = useCallback(() => {
        router.get('/queries');
    }, []);

    // Selection handlers
    const toggleAll = useCallback(() => {
        if (selected.length === queries.data.length) {
            setSelected([]);
        } else {
            setSelected(queries.data.map(q => q.id));
        }
    }, [selected, queries.data]);

    const toggleSelect = useCallback((id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    }, []);

    const clearSelection = useCallback(() => {
        setSelected([]);
        setBulkStatusOpen(false);
        setBulkAssignOpen(false);
    }, []);

    // Status change handler
    const handleStatusChange = useCallback((queryId, newStatus) => {
        router.patch(`/queries/${queryId}/status`, { status: newStatus }, {
            preserveScroll: true,
            onSuccess: () => toast.success('Status updated'),
        });
    }, []);

    // Delete handler
    const handleDelete = useCallback(() => {
        if (!queryToDelete) return;
        router.delete(`/queries/${queryToDelete.id}`, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Query deleted');
                setShowDeleteModal(false);
                setQueryToDelete(null);
            },
        });
    }, [queryToDelete]);

    // Bulk actions
    const handleBulkStatus = useCallback((status) => {
        router.post('/queries/bulk-status', { ids: selected, status }, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`${selected.length} queries updated to ${statuses[status]}`);
                clearSelection();
            },
        });
    }, [selected, statuses, clearSelection]);

    const handleBulkAssign = useCallback((userId) => {
        router.post('/queries/bulk-assign', { ids: selected, assigned_to: userId }, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`${selected.length} queries reassigned`);
                clearSelection();
            },
        });
    }, [selected, clearSelection]);

    const handleBulkDelete = useCallback(() => {
        router.post('/queries/bulk-delete', { ids: selected }, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`${selected.length} queries deleted`);
                clearSelection();
                setShowBulkDeleteModal(false);
            },
        });
    }, [selected, clearSelection]);

    // Select options for react-select
    const priorityOptions = useMemo(() =>
        Object.entries(priorities).map(([value, label]) => ({ value, label })),
    [priorities]);

    const sourceOptions = useMemo(() =>
        Object.entries(sources).map(([value, label]) => ({ value, label })),
    [sources]);

    const typeOptions = useMemo(() =>
        Object.entries(types).map(([value, label]) => ({ value, label })),
    [types]);

    const userOptions = useMemo(() =>
        users.map(u => ({ value: u.id, label: u.name })),
    [users]);

    // Resolve destinations
    const getDestNames = useCallback((destIds) => {
        if (!destIds || !Array.isArray(destIds)) return [];
        return destIds
            .map(id => destinationNames[id])
            .filter(Boolean);
    }, [destinationNames]);

    // Quick filter config
    const quickFilters = [
        { key: 'today', label: 'Today', icon: 'bi-calendar-day', count: quickFilterCounts.today },
        { key: 'this_week', label: 'This Week', icon: 'bi-calendar-week', count: quickFilterCounts.this_week },
        { key: 'my_queries', label: 'My Queries', icon: 'bi-person-check', count: quickFilterCounts.my_queries },
        { key: 'overdue_followup', label: 'Overdue', icon: 'bi-alarm', count: quickFilterCounts.overdue_followup },
    ];

    // Pipeline order
    const pipelineStatuses = [
        { key: 'all', label: 'All', count: totalCount },
        ...Object.entries(statuses).map(([key, label]) => ({
            key,
            label,
            count: statusCounts[key] || 0,
        })),
    ];

    return (
        <AppLayout title="Queries">
            <PageHeader
                title="Queries & Leads"
                subtitle="Sales pipeline & query management"
                breadcrumbs={[
                    { label: 'CRM' },
                    { label: 'Queries' },
                ]}
                actions={
                    <Link href="/queries/create" className="btn btn-primary btn-sm">
                        <i className="bi bi-plus-lg me-1"></i>
                        New Query
                    </Link>
                }
            />

            {/* A. Pipeline Status Cards */}
            <div className="qi-pipeline mb-3">
                {pipelineStatuses.map((s) => {
                    const isActive = s.key === 'all'
                        ? !filters.status && !filters.quick
                        : filters.status === s.key;
                    return (
                        <motion.div
                            key={s.key}
                            className={`qi-pipeline-card status-${s.key} ${isActive ? 'active' : ''}`}
                            onClick={() => applyFilter('status', s.key === 'all' ? '' : s.key)}
                            whileHover={{ y: -2, scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                        >
                            <div className="qi-pipeline-count">{s.count}</div>
                            <div className="qi-pipeline-label">{s.label}</div>
                            {isActive && (
                                <motion.div
                                    layoutId="pipeline-active-ring"
                                    style={{
                                        position: 'absolute',
                                        inset: -2,
                                        borderRadius: '0.625rem',
                                        border: '2px solid rgba(255,255,255,0.6)',
                                        pointerEvents: 'none',
                                    }}
                                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                />
                            )}
                        </motion.div>
                    );
                })}
            </div>

            {/* B. Quick Filters + Bulk Actions */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <div className="qi-quick-filters">
                    {quickFilters.map((qf) => (
                        <button
                            key={qf.key}
                            className={`qi-chip ${filters.quick === qf.key ? 'active' : ''}`}
                            onClick={() => applyFilter('quick', filters.quick === qf.key ? '' : qf.key)}
                        >
                            <i className={qf.icon}></i>
                            {qf.label}
                            <span className="qi-chip-count">{qf.count}</span>
                        </button>
                    ))}
                </div>

                <AnimatePresence>
                    {selected.length > 0 && (
                        <motion.div
                            className="qi-bulk-bar"
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                        >
                            <span className="qi-bulk-count">
                                {selected.length} selected
                            </span>
                            <div className="qi-bulk-actions">
                                <div className="position-relative">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => { setBulkStatusOpen(!bulkStatusOpen); setBulkAssignOpen(false); }}
                                    >
                                        <i className="bi bi-arrow-repeat me-1"></i>Status
                                    </button>
                                    {bulkStatusOpen && (
                                        <div className="dropdown-menu show position-absolute" style={{ zIndex: 30, top: '100%', left: 0, minWidth: '160px' }}>
                                            {Object.entries(statuses).map(([key, label]) => (
                                                <button
                                                    key={key}
                                                    className="dropdown-item small"
                                                    onClick={() => handleBulkStatus(key)}
                                                >
                                                    <span
                                                        className="d-inline-block rounded-circle me-2"
                                                        style={{ width: 8, height: 8, background: STATUS_HEX[key] }}
                                                    ></span>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="position-relative">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => { setBulkAssignOpen(!bulkAssignOpen); setBulkStatusOpen(false); }}
                                    >
                                        <i className="bi bi-person-plus me-1"></i>Assign
                                    </button>
                                    {bulkAssignOpen && (
                                        <div className="dropdown-menu show position-absolute" style={{ zIndex: 30, top: '100%', right: 0, minWidth: '180px' }}>
                                            {users.map((u) => (
                                                <button
                                                    key={u.id}
                                                    className="dropdown-item small"
                                                    onClick={() => handleBulkAssign(u.id)}
                                                >
                                                    {u.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setShowBulkDeleteModal(true)}
                                >
                                    <i className="bi bi-trash me-1"></i>Delete
                                </button>
                                <button
                                    className="btn btn-sm btn-light"
                                    onClick={clearSelection}
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* C. Advanced Filters */}
            <div className="card mb-3">
                <div className="card-body py-2 px-3 qi-filter-row">
                    <div className="row g-2 align-items-center">
                        <div className="col-md-2">
                            <SearchInput
                                value={filters.search || ''}
                                onSearch={(val) => applyFilter('search', val)}
                                placeholder="Search queries..."
                            />
                        </div>
                        <div className="col-md col-6">
                            <Select
                                styles={selectStyles}
                                options={priorityOptions}
                                value={priorityOptions.find(o => o.value === filters.priority) || null}
                                onChange={(opt) => applyFilter('priority', opt?.value || '')}
                                isClearable
                                placeholder="Priority"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div className="col-md col-6">
                            <Select
                                styles={selectStyles}
                                options={sourceOptions}
                                value={sourceOptions.find(o => o.value === filters.source) || null}
                                onChange={(opt) => applyFilter('source', opt?.value || '')}
                                isClearable
                                placeholder="Source"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div className="col-md col-6">
                            <Select
                                styles={selectStyles}
                                options={typeOptions}
                                value={typeOptions.find(o => o.value === filters.type) || null}
                                onChange={(opt) => applyFilter('type', opt?.value || '')}
                                isClearable
                                placeholder="Type"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div className="col-md col-6">
                            <Select
                                styles={selectStyles}
                                options={userOptions}
                                value={userOptions.find(o => o.value == filters.assigned_to) || null}
                                onChange={(opt) => applyFilter('assigned_to', opt?.value || '')}
                                isClearable
                                placeholder="Assigned To"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div className="col-md col-6">
                            <DatePicker
                                selected={filters.date_from ? new Date(filters.date_from) : null}
                                onChange={(date) => applyFilter('date_from', date ? format(date, 'yyyy-MM-dd') : '')}
                                placeholderText="Created from"
                                dateFormat="dd MMM yy"
                                isClearable
                                className="form-control form-control-sm"
                            />
                        </div>
                        <div className="col-md col-6">
                            <DatePicker
                                selected={filters.date_to ? new Date(filters.date_to) : null}
                                onChange={(date) => applyFilter('date_to', date ? format(date, 'yyyy-MM-dd') : '')}
                                placeholderText="Created to"
                                dateFormat="dd MMM yy"
                                isClearable
                                className="form-control form-control-sm"
                            />
                        </div>
                        <div className="col-auto">
                            {hasActiveFilters && (
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={clearFilters}
                                    title="Clear all filters"
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* D. Dense Table */}
            <div className="card">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 qi-table">
                        <thead>
                            <tr>
                                <th style={{ width: 36 }}>
                                    <input
                                        type="checkbox"
                                        className="qi-checkbox"
                                        checked={queries.data.length > 0 && selected.length === queries.data.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th>ID</th>
                                <th>Travel</th>
                                <th>Destination</th>
                                <th>Client / Guest</th>
                                <th>Created</th>
                                <th>Status</th>
                                <th>Ops</th>
                                <th>Sales</th>
                                <th>Amount</th>
                                <th style={{ width: 80 }} className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {queries.data.length === 0 ? (
                                <tr>
                                    <td colSpan="11">
                                        <EmptyState
                                            icon="bi-collection"
                                            title="No queries found"
                                            description={hasActiveFilters ? 'Try adjusting your filters.' : 'Start by creating your first query.'}
                                            action={
                                                !hasActiveFilters && (
                                                    <Link href="/queries/create" className="btn btn-primary btn-sm">
                                                        <i className="bi bi-plus-lg me-1"></i>New Query
                                                    </Link>
                                                )
                                            }
                                        />
                                    </td>
                                </tr>
                            ) : (
                                queries.data.map((q, idx) => {
                                    const assignedUser = getAssignedTo(q);
                                    const creator = getCreatedBy(q);
                                    const dests = getDestNames(q.destinations);
                                    const phone = q.contact_phone || q.client?.phone;
                                    const clientName = q.guest_name || q.client?.name || '-';
                                    const rowClass = q.is_stale ? 'qi-row-stale'
                                        : q.status === 'cancelled' ? 'qi-row-cancelled'
                                        : q.status === 'lost' ? 'qi-row-lost'
                                        : '';

                                    return (
                                        <motion.tr
                                            key={q.id}
                                            className={rowClass}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: idx * 0.02, duration: 0.25 }}
                                        >
                                            {/* 1. Checkbox */}
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="qi-checkbox"
                                                    checked={selected.includes(q.id)}
                                                    onChange={() => toggleSelect(q.id)}
                                                />
                                            </td>

                                            {/* 2. ID + Title + Indicators */}
                                            <td>
                                                <Link href={`/queries/${q.id}`} className="qi-query-id">
                                                    {q.query_number}
                                                </Link>
                                                <div className="qi-title" title={q.title}>{q.title}</div>
                                                <div className="qi-indicators">
                                                    {q.quotations_count > 0 && (
                                                        <span className="qi-indicator indicator-quotation">
                                                            <i className="bi bi-file-text"></i>{q.quotations_count}
                                                        </span>
                                                    )}
                                                    {q.followups_count > 0 && (
                                                        <span className="qi-indicator indicator-followup">
                                                            <i className="bi bi-chat-dots"></i>{q.followups_count}
                                                        </span>
                                                    )}
                                                    {q.unread_communications_count > 0 && (
                                                        <span className="qi-indicator indicator-unread">
                                                            <i className="bi bi-envelope"></i>{q.unread_communications_count}
                                                        </span>
                                                    )}
                                                </div>
                                                {/* TAT Warning */}
                                                {q.is_stale && (
                                                    <div className="qi-tat-warning mt-1">
                                                        <i className="bi bi-exclamation-triangle"></i>
                                                        Untouched for {q.stale_minutes >= 1440
                                                            ? Math.floor(q.stale_minutes / 1440) + 'd'
                                                            : q.stale_minutes >= 60
                                                            ? Math.floor(q.stale_minutes / 60) + 'h'
                                                            : q.stale_minutes + 'm'}
                                                    </div>
                                                )}
                                            </td>

                                            {/* 3. Travel */}
                                            <td>
                                                <div className="small">
                                                    <div>
                                                        <i className="bi bi-calendar3 me-1 text-muted" style={{ fontSize: '0.625rem' }}></i>
                                                        {formatDate(q.travel_date)}
                                                    </div>
                                                    {q.nights > 0 && (
                                                        <div>
                                                            <i className="bi bi-moon-stars me-1 text-muted" style={{ fontSize: '0.625rem' }}></i>
                                                            {q.nights}N
                                                        </div>
                                                    )}
                                                    <div>
                                                        <i className="bi bi-people me-1 text-muted" style={{ fontSize: '0.625rem' }}></i>
                                                        {q.adults}A
                                                        {q.children > 0 && <span>+{q.children}C</span>}
                                                        {q.infants > 0 && <span>+{q.infants}I</span>}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 4. Destination + Priority + Type */}
                                            <td>
                                                <div className="d-flex flex-wrap gap-1 mb-1">
                                                    {dests.slice(0, 2).map((name, i) => (
                                                        <span key={i} className="qi-destination-badge">{name}</span>
                                                    ))}
                                                    {dests.length > 2 && (
                                                        <span className="qi-destination-badge" title={dests.slice(2).join(', ')}>
                                                            +{dests.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {q.priority && (
                                                        <span className={`qi-priority-badge priority-${q.priority}`}>
                                                            {q.priority === 'hot' && <i className="bi bi-fire"></i>}
                                                            {priorities[q.priority]}
                                                        </span>
                                                    )}
                                                    {q.travel_type && travelTypes[q.travel_type] && (
                                                        <span className="qi-type-badge">{travelTypes[q.travel_type]}</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* 5. Client */}
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Avatar name={clientName} size="xs" />
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="fw-medium small text-truncate" style={{ maxWidth: 120 }}>
                                                            {clientName}
                                                            {q.client?.company && (
                                                                <span className="qi-client-type type-b2b ms-1">B2B</span>
                                                            )}
                                                        </div>
                                                        {phone && (
                                                            <div className="text-muted" style={{ fontSize: '0.6875rem' }}>
                                                                {phone}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 6. Created */}
                                            <td>
                                                <div className="small">
                                                    {q.created_at ? formatDistanceToNow(new Date(q.created_at), { addSuffix: true }) : '-'}
                                                </div>
                                                {creator && (
                                                    <div className="text-muted" style={{ fontSize: '0.6875rem' }}>
                                                        by {creator.name}
                                                    </div>
                                                )}
                                            </td>

                                            {/* 7. Status */}
                                            <td>
                                                <select
                                                    className={`form-select form-select-sm qi-status-select status-${q.status}`}
                                                    value={q.status}
                                                    onChange={(e) => handleStatusChange(q.id, e.target.value)}
                                                >
                                                    {Object.entries(statuses).map(([key, label]) => (
                                                        <option key={key} value={key}>{label}</option>
                                                    ))}
                                                </select>
                                            </td>

                                            {/* 8. Ops */}
                                            <td>
                                                {q.operations_user ? (
                                                    <span className="qi-user-badge user-ops">
                                                        {q.operations_user.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </td>

                                            {/* 9. Sales */}
                                            <td>
                                                {assignedUser ? (
                                                    <span className="qi-user-badge user-sales">
                                                        {assignedUser.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </td>

                                            {/* 10. Amount */}
                                            <td>
                                                <div className="qi-amount">{formatCurrency(q.quoted_amount)}</div>
                                                {q.budget > 0 && (
                                                    <div className="qi-budget">Budget: {formatCurrency(q.budget)}</div>
                                                )}
                                            </td>

                                            {/* 11. Actions */}
                                            <td className="text-end">
                                                <RowActions actions={[
                                                    { icon: 'bi-eye', label: 'View', onClick: () => router.visit(`/queries/${q.id}`) },
                                                    { icon: 'bi-pencil', label: 'Edit', onClick: () => router.visit(`/queries/${q.id}/edit`) },
                                                    { icon: 'bi-file-earmark-plus', label: 'New Quotation', onClick: () => router.visit(`/queries/${q.id}/quotations/create`) },
                                                    ...(phone ? [
                                                        { divider: true },
                                                        {
                                                            icon: 'bi-whatsapp',
                                                            label: 'WhatsApp',
                                                            whatsapp: true,
                                                            href: `https://wa.me/${cleanPhone(phone)}?text=${encodeURIComponent(`Hi, regarding your query ${q.query_number}`)}`,
                                                            target: '_blank',
                                                            rel: 'noopener noreferrer',
                                                        },
                                                    ] : []),
                                                    { divider: true },
                                                    { icon: 'bi-trash', label: 'Delete', danger: true, onClick: () => { setQueryToDelete(q); setShowDeleteModal(true); } },
                                                ]} />
                                            </td>
                                        </motion.tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    data={queries}
                    showPerPage
                    onPerPageChange={(value) => applyFilter('per_page', value)}
                />
            </div>

            {/* Delete Single Query Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => { setShowDeleteModal(false); setQueryToDelete(null); }}
                onConfirm={handleDelete}
                title="Delete Query"
                message={`Are you sure you want to delete query ${queryToDelete?.query_number}? This action cannot be undone.`}
                confirmText="Delete"
                variant="danger"
            />

            {/* Bulk Delete Modal */}
            <ConfirmModal
                show={showBulkDeleteModal}
                onHide={() => setShowBulkDeleteModal(false)}
                onConfirm={handleBulkDelete}
                title="Delete Queries"
                message={`Are you sure you want to delete ${selected.length} selected queries? This action cannot be undone.`}
                confirmText="Delete All"
                variant="danger"
            />

            <Toaster position="top-right" richColors />
        </AppLayout>
    );
}
